/**
 * SWFL city data for local landing pages (/home-care/:citySlug).
 * Content is authentic enough for real prospects yet keyword-dense
 * for local search dominance. Each page ~450-600 words.
 */
export const CITIES = [
  {
    slug: "naples",
    name: "Naples",
    zips: "34102, 34103, 34105, 34108, 34109, 34110, 34112, 34119",
    neighborhoods: "Old Naples, Port Royal, Park Shore, Pelican Bay, Vanderbilt Beach, Aqualane Shores, Coquina Sands",
    heroTagline: "White-glove in-home senior care for Naples families.",
    intro:
      "Naples has always attracted people who value quality — quiet mornings on Fifth Avenue South, sunset walks along Vanderbilt Beach, an unhurried pace lived on their own terms. When a parent or spouse needs a little more support, that standard shouldn't change. Elite Home Care by Hortense provides concierge-level in-home care for Naples seniors, so aging at home remains dignified, safe, and beautifully ordinary.",
    lifestyleParagraph:
      "Our Naples caregivers understand the community. They know the difference between a morning drive down Gordon Drive and an afternoon visit to Waterside Shops. They respect the routines that make a Naples life feel like home — the club schedule, the winter houseguests, the standing appointment with a longtime physician on Goodlette-Frank. Every visit is planned around your loved one's world, not around a franchise checklist.",
    servicesParagraph:
      "Whether your family needs a few hours of companion care, help managing medications after a Naples Community Hospital discharge, overnight assistance during a difficult stretch, or specialized Alzheimer's and dementia support, our caregivers arrive prepared, on time, and matched to your loved one's personality.",
    whyLocal: [
      "Serving Old Naples, Port Royal, Park Shore, Pelican Bay, Vanderbilt Beach, Aqualane Shores, and Coquina Sands.",
      "Familiar with Naples Community Hospital, Physicians Regional, and NCH North discharge planning.",
      "Discreet — we understand privacy matters in this community.",
      "Consistent caregiver matching. Not a rotating door of strangers.",
    ],
    faqExtra: {
      question: "Do you accept clients in gated communities like Port Royal or Pelican Bay?",
      answer:
        "Absolutely. Our caregivers are professional, well-groomed, background-checked, and experienced with guardhouse protocols and residence associations throughout Naples.",
    },
  },
  {
    slug: "fort-myers",
    name: "Fort Myers",
    zips: "33901, 33907, 33908, 33912, 33913, 33916, 33919, 33966",
    neighborhoods: "Downtown River District, McGregor Boulevard, Fort Myers Beach, Iona, Whiskey Creek, Gulf Harbour",
    heroTagline: "Trusted in-home care for Fort Myers seniors and families.",
    intro:
      "Fort Myers has always been a city that takes care of its own. From the historic homes along McGregor Boulevard to the waterfront neighborhoods of Iona and Gulf Harbour, families here want reliable, family-first care for the parents and grandparents who raised them. Elite Home Care by Hortense delivers exactly that — trained, background-checked caregivers who show up on time, every time.",
    lifestyleParagraph:
      "Our Fort Myers caregivers assist with the everyday rhythms that keep life at home comfortable: the morning coffee on the lanai, the standing hair appointment, the drive to Lee Memorial or HealthPark for a follow-up. We build routines around your loved one's life — not our schedule.",
    servicesParagraph:
      "Personal care, companion care, meal preparation, medication reminders, transportation, light housekeeping, and specialized Alzheimer's and dementia care — all delivered in-home by caregivers who feel more like family than staff.",
    whyLocal: [
      "Serving downtown Fort Myers, McGregor, Fort Myers Beach, Iona, Whiskey Creek, and Gulf Harbour.",
      "Coordination with Lee Memorial, HealthPark, and Gulf Coast Medical Center after hospital discharge.",
      "Flexible schedules — a few hours, 24/7, or live-in.",
      "Same caregiver on the same days — consistency your loved one can count on.",
    ],
    faqExtra: {
      question: "How quickly can care start in Fort Myers?",
      answer:
        "Often within 24-48 hours. After your free consultation, we present matched caregivers, and once you approve, care can begin the very next day for most non-specialized needs.",
    },
  },
  {
    slug: "cape-coral",
    name: "Cape Coral",
    zips: "33904, 33909, 33914, 33915, 33990, 33991, 33993",
    neighborhoods: "Yacht Club, Cape Harbour, Rose Garden, Pelican, Sandoval, Tarpon Point",
    heroTagline: "In-home senior care built around Cape Coral life.",
    intro:
      "Cape Coral's canal-front homes and quiet, family neighborhoods make it one of the best places in Florida to age in place. But even the most peaceful life sometimes needs a little help — and finding a caregiver you can trust is everything. Elite Home Care by Hortense provides warm, professional in-home care throughout Cape Coral, from the Yacht Club district to Tarpon Point.",
    lifestyleParagraph:
      "Whether your parent still enjoys mornings on the dock, weekly trips to Publix, or a standing card game with neighbors, our caregivers preserve the routines that make Cape Coral home. We adapt to your loved one — never the other way around.",
    servicesParagraph:
      "From a few hours of companion care to full 24/7 support, we deliver every one of our services — personal care, meal prep, medication reminders, light housekeeping, transportation, and dementia care — with the calm consistency families have come to expect from Hortense's team.",
    whyLocal: [
      "Serving the Yacht Club, Cape Harbour, Pelican, Sandoval, Tarpon Point, and surrounding neighborhoods.",
      "Familiar with Cape Coral Hospital and Lee Health discharge processes.",
      "Comfortable in single-family homes, condos, and canal-front residences.",
      "Consistent, matched caregivers you'll come to know by name.",
    ],
    faqExtra: {
      question: "Do you offer overnight or 24-hour care in Cape Coral?",
      answer:
        "Yes. We staff overnight, live-in, and 24-hour rotating care in Cape Coral — with the same rigorous caregiver vetting as our daytime hourly service.",
    },
  },
  {
    slug: "bonita-springs",
    name: "Bonita Springs",
    zips: "34134, 34135",
    neighborhoods: "Bonita Bay, Barefoot Beach, Pelican Landing, Palmira, Bonita Springs Village",
    heroTagline: "Concierge in-home care for Bonita Springs families.",
    intro:
      "From Bonita Bay to Barefoot Beach, Bonita Springs is home to families who expect more from the people they trust with their loved ones. Elite Home Care by Hortense delivers a concierge standard of in-home care — upscale, discreet, and always centered on the client's dignity.",
    lifestyleParagraph:
      "Our Bonita Springs caregivers become familiar faces in your loved one's world. They know when it's time to walk the neighborhood, when the grandchildren are visiting, and how much sugar goes in the afternoon tea. That level of attention isn't a nice-to-have — it's what makes premium care actually feel premium.",
    servicesParagraph:
      "Personal care, companion care, medication management, meal preparation, transportation, light housekeeping, and specialized Alzheimer's and dementia care — offered flexibly from a few hours per week up to full-time live-in care.",
    whyLocal: [
      "Serving Bonita Bay, Barefoot Beach, Pelican Landing, Palmira, and greater Bonita Springs.",
      "Experienced in gated communities and country club residences.",
      "Coordination with NCH Bonita and specialty providers along US-41.",
      "Absolute discretion — professional, well-presented caregivers who understand this community.",
    ],
    faqExtra: {
      question: "Can you support a client with a live-in caregiver in Bonita Springs?",
      answer:
        "Yes. Live-in and 24/7 rotating care are among our most-requested services in Bonita Springs, especially for clients transitioning back home after a hospital stay.",
    },
  },
  {
    slug: "marco-island",
    name: "Marco Island",
    zips: "34145",
    neighborhoods: "Old Marco, Hideaway Beach, Marco Shores, South Beach",
    heroTagline: "Reliable in-home senior care on Marco Island.",
    intro:
      "Life on Marco Island has a rhythm all its own — sunrise walks on the beach, boats leaving the marina, quiet evenings on the lanai. Elite Home Care by Hortense helps your loved one keep that rhythm going with in-home care that is warm, professional, and refreshingly consistent.",
    lifestyleParagraph:
      "Our Marco Island caregivers work with island time — patient, unhurried, attentive to the small details that make a day feel good. They know the drive to Marco Healthcare Center, the best pharmacies, and the routines that make island life feel like home.",
    servicesParagraph:
      "We provide every service you would expect from a premium in-home care agency — personal care, companionship, medication reminders, meal preparation, light housekeeping, transportation, and specialized memory care — on the island's schedule, not ours.",
    whyLocal: [
      "Serving all of Marco Island, including Old Marco, Hideaway Beach, and Marco Shores.",
      "Reliable coverage even during peak season traffic and weather events.",
      "Coordination with Marco Healthcare Center and NCH Naples.",
      "Consistent caregiver matching — the same trusted faces, week after week.",
    ],
    faqExtra: {
      question: "Do your caregivers travel to Marco Island reliably during season?",
      answer:
        "Yes. We plan Marco Island staffing around seasonal traffic patterns so your caregiver arrives on time — every shift, every day, all year.",
    },
  },
  {
    slug: "estero",
    name: "Estero",
    zips: "33928, 33967, 34134, 34135",
    neighborhoods: "Grandezza, Wildcat Run, The Brooks, Corkscrew Shores, Coconut Point area",
    heroTagline: "Family-first in-home care for Estero seniors.",
    intro:
      "Estero has grown into one of Southwest Florida's most desirable communities — beautiful gated neighborhoods, first-class amenities, and the peace of mind that comes from a quieter pace. When it's time to arrange in-home care for a parent or spouse in Estero, families want a provider that matches the standards of the community. Elite Home Care by Hortense is that provider.",
    lifestyleParagraph:
      "Our Estero caregivers understand the balance of active-adult life — the tennis mornings at Grandezza, the walks around Corkscrew Shores, the Wednesday lunch with friends at Coconut Point. Care is delivered around your loved one's world, quietly and consistently.",
    servicesParagraph:
      "From short companion visits to full 24/7 live-in care, we offer every one of our core services — personal care, meal prep, medication support, transportation, light housekeeping, and Alzheimer's and dementia care — with the same warmth and consistency that defines everything Hortense's team does.",
    whyLocal: [
      "Serving Grandezza, Wildcat Run, The Brooks, Corkscrew Shores, and communities around Coconut Point.",
      "Experienced with gated-community access and country-club residences.",
      "Coordination with Lee Health facilities and specialty care in Bonita and Fort Myers.",
      "Consistent caregivers — the same warm face on the same days.",
    ],
    faqExtra: {
      question: "Can you help coordinate care after a hospital stay in Estero?",
      answer:
        "Yes. We work closely with families during hospital discharge to ensure caregivers, medications, meals, and transportation are all in place the day your loved one returns home.",
    },
  },
];

export const CITY_SLUGS = CITIES.map((c) => c.slug);

export function getCity(slug) {
  return CITIES.find((c) => c.slug === slug);
}
